import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useSpring, useTransform, useMotionValue } from 'framer-motion';
import { ArrowRight, Check, User, Users, Star, ArrowUpRight, Sparkles } from 'lucide-react';
import './Hero_mobile.css';

// Organic blob backgrounds with enhanced dynamism
const OrganicBlobs = () => (
  <div className="mobile-organic-blobs">
    <div className="mobile-organic-blob blob-1"></div>
    <div className="mobile-organic-blob blob-2"></div>
    <div className="mobile-organic-blob blob-3"></div>
    <div className="mobile-organic-blob blob-4"></div>
  </div>
);

// Enhanced floating particles with varied colors and sizes
const EnhancedParticles = () => (
  <div className="mobile-enhanced-particles">
    {[...Array(20)].map((_, i) => (
      <div 
        key={i} 
        className={`mobile-enhanced-particle particle-${i % 6}`}
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          width: `${Math.random() * 6 + 2}px`,
          height: `${Math.random() * 6 + 2}px`,
          animationDelay: `${Math.random() * 10}s`,
          animationDuration: `${Math.random() * 15 + 10}s`
        }}
      />
    ))}
  </div>
);

// Updated HeadlineSlider with tighter word spacing
// Updated HeadlineSlider without icons next to the headline
const HeadlineSlider = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [previousIndex, setPreviousIndex] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const headlines = [
      "Save $450 on Premium Phone Plans",
      "Group Together and Save!"
    ];
    
    // Content-aware themes - keeping these for the underline and accent colors
    const headlineThemes = [
      { 
        accent: "#7FFAFF", 
        accentBg: "linear-gradient(135deg, rgba(0, 209, 224, 0.2), rgba(0, 122, 133, 0.05))"
      },
      { 
        accent: "#7FFAFF",
        accentBg: "linear-gradient(135deg, rgba(255, 71, 117, 0.2), rgba(229, 0, 68, 0.05))"
      }
    ];
  
    useEffect(() => {
      const timer = setInterval(() => {
        setPreviousIndex(currentIndex);
        setIsTransitioning(true);
        setCurrentIndex((prevIndex) => (prevIndex + 1) % headlines.length);
        
        // Reset transition flag after animation completes
        setTimeout(() => {
          setIsTransitioning(false);
        }, 1000);
      }, 6000);
  
      return () => clearInterval(timer);
    }, [currentIndex]);
    
    // Split headlines into words for animations
    const currentWords = headlines[currentIndex].split(' ');
    
    // Create staggered delay for word animations
    const getWordDelay = (index) => 0.06 * index; // Reduced delay for snappier animations
    
    const currentTheme = headlineThemes[currentIndex];
    const previousTheme = headlineThemes[previousIndex];
  
    return (
      <div className="mobile-headline-slider">
        {/* Accent line */}
        <motion.div 
          className="headline-accent-line"
          animate={{ 
            background: currentTheme.accentBg,
            x: isTransitioning ? [0, -5, 0] : 0
          }}
          transition={{ 
            duration: 0.8,
            background: { duration: 1.2 }
          }}
        />
        
        <div className="headline-content">
          {/* Removed the icon indicator - this gives the headline more space */}
          
          {/* Headline text with tighter spacing */}
          <div className="headline-text-container headline-text-container-fullwidth">
            <AnimatePresence mode="wait">
              <motion.h1 
                key={currentIndex}
                className="mobile-title"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
              >
                {currentWords.map((word, index) => (
                  <React.Fragment key={`${currentIndex}-${index}`}>
                    <motion.span
                      className={`headline-word ${index === currentWords.length - 1 ? 'accent' : ''}`}
                      initial={{ opacity: 0, x: -5, filter: "blur(3px)" }} // Reduced motion
                      animate={{ 
                        opacity: 1, 
                        x: 0,
                        filter: "blur(0px)"
                      }}
                      transition={{ 
                        duration: 0.35, // Faster animation
                        delay: getWordDelay(index),
                        ease: [0.22, 1, 0.36, 1]
                      }}
                    >
                      {word}
                    </motion.span>
                    {/* Tighter spacing between words */}
                    {index < currentWords.length - 1 && (
                      <span className="headline-space"> </span>
                    )}
                  </React.Fragment>
                ))}
              </motion.h1>
            </AnimatePresence>
            
            {/* Dynamic underline */}
            <motion.div 
              className="headline-underline"
              initial={{ width: "0%", backgroundColor: currentTheme.accent }}
              animate={{ 
                width: currentIndex === 0 ? "40%" : "30%", 
                backgroundColor: currentTheme.accent 
              }}
              transition={{ 
                duration: 0.8, 
                delay: 0.2,
                ease: [0.33, 1, 0.68, 1]
              }}
            />
          </div>
        </div>
      </div>
    );
  };

// Interactive savings card component with enhanced animations
const InteractiveSavingsCard = () => {
  const cardRef = useRef(null);
  const [touchPosition, setTouchPosition] = useState({ x: 0, y: 0 });
  const [isInteracting, setIsInteracting] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  
  const handleTouchMove = (e) => {
    if (!cardRef.current || !e.touches) return;
    
    const touch = e.touches[0];
    const rect = cardRef.current.getBoundingClientRect();
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;
    
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    
    // Gentler rotation effect for mobile
    const rotateX = (y - centerY) / 30;
    const rotateY = (centerX - x) / 30;
    
    setTouchPosition({ rotateX, rotateY });
  };
  
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      const progress = Math.min(Math.max(scrollY / (windowHeight * 0.5), 0), 1);
      setScrollProgress(progress);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const springConfig = { damping: 18, stiffness: 300 };
  const rotateXSpring = useSpring(0, springConfig);
  const rotateYSpring = useSpring(0, springConfig);
  
  useEffect(() => {
    rotateXSpring.set(isInteracting ? touchPosition.rotateX : 0);
    rotateYSpring.set(isInteracting ? touchPosition.rotateY : 0);
  }, [touchPosition, isInteracting, rotateXSpring, rotateYSpring]);
  
  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;
    
    const handleTouchStart = () => setIsInteracting(true);
    const handleTouchEnd = () => setIsInteracting(false);
    
    card.addEventListener('touchmove', handleTouchMove, { passive: true });
    card.addEventListener('touchstart', handleTouchStart, { passive: true });
    card.addEventListener('touchend', handleTouchEnd, { passive: true });
    
    return () => {
      card.removeEventListener('touchmove', handleTouchMove);
      card.removeEventListener('touchstart', handleTouchStart);
      card.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);
  
  // Parallax effect based on scroll
  const parallaxY = useTransform(
    useMotionValue(scrollProgress),
    [0, 1],
    [0, -30]
  );
  
  return (
    <motion.div 
      ref={cardRef}
      className="mobile-savings-card ultra-glass"
      style={{
        rotateX: rotateXSpring,
        rotateY: rotateYSpring,
        transformPerspective: 1000,
        transformStyle: "preserve-3d",
        y: parallaxY
      }}
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ 
        duration: 0.8, 
        delay: 0.4,
        ease: "easeOut" 
      }}
      whileHover={{ 
        scale: 1.02,
        transition: { duration: 0.3 }
      }}
    >
      <div className="mobile-card-shine"></div>
      <div className="mobile-card-noise"></div>
      <motion.div style={{ position: "relative", zIndex: 2 }}>
        <motion.h2 
          className="mobile-savings-title"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.5 }}
        >
          From Solo to Savings
        </motion.h2>
        
        <div className="mobile-price-comparison">
          <motion.div 
            className="mobile-price-box"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.7, duration: 0.6 }}
            whileHover={{ 
              scale: 1.05,
              transition: { type: "spring", stiffness: 400, damping: 10 }
            }}
            whileTap={{ scale: 0.95 }}
          >
            <motion.div 
              className="mobile-price-icon individual ultra-glass-light"
              whileHover={{ scale: 1.1 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <User size={20} color="white" />
              <div className="mobile-icon-glow"></div>
            </motion.div>
            <span className="mobile-price-amount">$80</span>
            <span className="mobile-price-label">By Yourself</span>
          </motion.div>
          
          <motion.div 
            className="mobile-price-arrow"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.5 }}
          >
            <motion.svg 
              width="28" 
              height="28" 
              viewBox="0 0 40 40" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              animate={{ 
                x: [0, 10, 0],
                opacity: [0.7, 1, 0.7] 
              }}
              transition={{ 
                duration: 2, 
                repeat: Infinity,
                repeatType: "loop",
                ease: "easeInOut" 
              }}
            >
              <path d="M8 20H32M32 20L22 10M32 20L22 30" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </motion.svg>
          </motion.div>
          
          <motion.div 
            className="mobile-price-box"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            whileHover={{ 
              scale: 1.05,
              transition: { type: "spring", stiffness: 400, damping: 10 }
            }}
            whileTap={{ scale: 0.95 }}
          >
            <motion.div 
              className="mobile-price-icon group ultra-glass-light"
              whileHover={{ scale: 1.1 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <Users size={20} color="white" />
              <div className="mobile-icon-glow"></div>
            </motion.div>
            <motion.span 
              className="mobile-price-amount highlight"
              animate={{ 
                scale: [1, 1.03, 1],
                textShadow: [
                  "0 0 5px rgba(0, 209, 224, 0.3)",
                  "0 0 15px rgba(0, 209, 224, 0.6)",
                  "0 0 5px rgba(0, 209, 224, 0.3)"
                ]
              }}
              transition={{ 
                duration: 2, 
                repeat: Infinity,
                repeatType: "loop",
                ease: "easeInOut" 
              }}
            >
              $40
            </motion.span>
            <span className="mobile-price-label">Group Plan</span>
          </motion.div>
        </div>
        
        <motion.div 
          className="mobile-savings-result ultra-glass-medium"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.6 }}
          whileHover={{ 
            y: -5,
            boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.15) inset, 0 0 30px rgba(0, 209, 224, 0.2) inset"
          }}
        >
          <h3>Your Potential Savings</h3>
          <div className="mobile-savings-numbers">
            <motion.div 
              className="mobile-savings-item"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2 }}
            >
              <span className="mobile-savings-label">Monthly</span>
              <motion.span 
                className="mobile-savings-amount"
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ 
                  delay: 1.3, 
                  duration: 0.5, 
                  type: "spring", 
                  stiffness: 300, 
                  damping: 10 
                }}
              >
                $40
              </motion.span>
            </motion.div>
            
            <div className="mobile-savings-divider"></div>
            
            <motion.div 
              className="mobile-savings-item"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.4 }}
            >
              <span className="mobile-savings-label">Annually</span>
              <motion.span 
                className="mobile-savings-amount"
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ 
                  delay: 1.5, 
                  duration: 0.5, 
                  type: "spring", 
                  stiffness: 300, 
                  damping: 10 
                }}
              >
                $480
              </motion.span>
            </motion.div>
          </div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

// Enhanced feature item with hover effects and context-awareness
const EnhancedFeatureItem = ({ mainText, supportingText, delay = 0, icon = null, accent = "#00D1E0" }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.div 
      className="mobile-feature"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.6, delay: 0.8 + delay * 0.2, ease: "easeOut" }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      whileHover={{ scale: 1.03, x: 5 }}
      whileTap={{ scale: 0.97 }}
      style={{
        borderLeft: `2px solid ${accent}`
      }}
    >
      <motion.div 
        className="mobile-feature-icon-container"
        animate={{ 
          boxShadow: isHovered 
            ? `0 15px 30px rgba(0, 209, 224, 0.3), 0 0 0 2px ${accent}80` 
            : "0 10px 20px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 209, 224, 0.2)"
        }}
        style={{
          background: `linear-gradient(135deg, ${accent}4D, ${accent}1A)`
        }}
      >
        {icon || <Check size={16} className="mobile-feature-icon" />}
        <motion.div 
          className="mobile-icon-glow"
          animate={{
            opacity: isHovered ? 1 : 0.6,
            scale: isHovered ? 1.2 : 1
          }}
          transition={{ duration: 0.5 }}
          style={{
            background: `radial-gradient(circle, ${accent}99, ${accent}00)`
          }}
        />
      </motion.div>
      <div className="mobile-feature-text-container">
        <motion.span 
          className="mobile-feature-text"
          animate={{
            color: isHovered ? "#ffffff" : "rgba(255, 255, 255, 0.95)"
          }}
          transition={{ duration: 0.3 }}
        >
          {mainText}
        </motion.span>
        <motion.span 
          className="mobile-feature-subtext"
          animate={{
            opacity: isHovered ? 1 : 0.85
          }}
          transition={{ duration: 0.3 }}
        >
          {supportingText}
        </motion.span>
      </div>
    </motion.div>
  );
};

// Modified Enhanced CTA Button with tighter styling
const EnhancedCTAButton = ({ children }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.button 
      className="mobile-cta mobile-glass-button"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.6, delay: 1.4, ease: "easeOut" }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      whileHover={{ 
        scale: 1.05
      }}
      whileTap={{ scale: 0.98 }}
    >
      <span className="mobile-button-text">{children}</span>
      <motion.div 
        className="mobile-button-icon-container"
        animate={{
          x: isHovered ? 5 : 0,
          backgroundColor: isHovered ? "rgba(255, 255, 255, 0.2)" : "rgba(255, 255, 255, 0)"
        }}
      >
        <motion.div
          animate={{
            x: [0, 5, 0],
            transition: {
              duration: 1.5,
              repeat: Infinity,
              repeatType: "loop",
              ease: "easeInOut"
            }
          }}
        >
          <ArrowUpRight size={18} className="mobile-button-icon" />
        </motion.div>
      </motion.div>
      <motion.div 
        className="mobile-button-glow" 
        animate={{
          opacity: isHovered ? 1 : 0
        }}
        transition={{ duration: 0.3 }}
      />
      
      {/* Interactive gradient border */}
      <div className="button-gradient-border"></div>
    </motion.button>
  );
};

// Modified Enhanced Customer Satisfaction component with tighter styling
const EnhancedCustomerSatisfaction = () => (
  <motion.div 
    className="mobile-satisfaction-container"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay: 1.6, ease: "easeOut" }}
    whileHover={{ 
      scale: 1.05,
      boxShadow: "0 15px 30px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(255, 255, 255, 0.15) inset"
    }}
  >
    <div className="mobile-stars-container">
      {[...Array(5)].map((_, i) => (
        <motion.div 
          key={i}
          initial={{ opacity: 0, scale: 0, rotate: -30 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ 
            delay: 1.8 + i * 0.1, 
            duration: 0.4,
            type: "spring",
            stiffness: 300
          }}
          whileHover={{ scale: 1.2, rotate: 5 }}
        >
          <Star size={12} className="mobile-star filled" />
        </motion.div>
      ))}
    </div>
    <span className="mobile-satisfaction-text">90% Customer Satisfaction</span>
    <div className="mobile-satisfaction-glow"></div>
  </motion.div>
);

const HeroMobile = () => {
  const [isVisible, setIsVisible] = useState(false);
  const heroRef = useRef(null);
  const [scrollY, setScrollY] = useState(0);
  
  useEffect(() => {
    setIsVisible(true);
    
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setScrollY(scrollPosition);
      
      const heroElement = heroRef.current;
      if (heroElement) {
        // Smoother parallax effect for mobile
        const blobs = heroElement.querySelectorAll('.mobile-organic-blob');
        blobs.forEach((blob, index) => {
          const speed = 0.015 + (index * 0.008); // Reduced speed for smoother effect
          const rotate = scrollPosition * 0.008; // Reduced rotation
          blob.style.transform = `translateY(${scrollPosition * speed}px) rotate(${rotate}deg)`;
        });
        
        // Subtle parallax for content elements
        const title = heroElement.querySelector('.mobile-headline-slider');
        const features = heroElement.querySelector('.mobile-features');
        const card = heroElement.querySelector('.mobile-savings-card');
        
        if (title) title.style.transform = `translateY(${scrollPosition * -0.04}px)`;
        if (features) features.style.transform = `translateY(${scrollPosition * -0.02}px)`;
        if (card) title.style.transform = `translateY(${scrollPosition * -0.01}px)`;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <section className="mobile-hero-section" ref={heroRef}>
      {/* Background elements */}
      <div className="mobile-noise-overlay"></div>
      <OrganicBlobs />
      <EnhancedParticles />
      
      {/* Color overlay gradients */}
      <div className="mobile-gradient-overlay gradient-1"></div>
      <div className="mobile-gradient-overlay gradient-2"></div>
      
      <div className="mobile-hero-container">
        <div className="mobile-hero-content">
          {/* Enhanced headline with seamless transitions */}
          <HeadlineSlider />
          
          <motion.p 
            className="mobile-subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }}
            transition={{ duration: 0.6, delay: 0.9, ease: "easeOut" }}
          >
            
          </motion.p>
          
          <InteractiveSavingsCard />
          
          <motion.div 
            className="mobile-features"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <EnhancedFeatureItem 
              mainText="No Credit Check" 
              supportingText="Join without affecting your credit score" 
              delay={0} 
              accent="#00D1E0"
            />
            <EnhancedFeatureItem 
              mainText="Save $480/Year" 
              supportingText="Real savings you can count on" 
              delay={1}
              accent="#FF4775" 
            />
            <EnhancedFeatureItem 
              mainText="5-Minute Setup" 
              supportingText="Start saving immediately" 
              delay={2}
              accent="#FFD700" 
            />
          </motion.div>
          
          {/* NEW: Compact layout for CTA and satisfaction rating */}
          <div className="action-satisfaction-container">
            <EnhancedCTAButton>
              See How It Works
            </EnhancedCTAButton>
            
            <EnhancedCustomerSatisfaction />
          </div>
        </div>
      </div>
      
      <motion.div 
        className="mobile-scroll-indicator"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2.0, duration: 0.5 }}
      >
        <div className="mobile-mouse">
          <div className="mobile-mouse-wheel"></div>
        </div>
        <motion.span
          animate={{
            y: [0, 5, 0],
            opacity: [0.7, 1, 0.7]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            repeatType: "loop",
            ease: "easeInOut"
          }}
        >
          Scroll to explore
        </motion.span>
      </motion.div>
    </section>
  );
};

export default HeroMobile;